import { type MapViewState, WebMercatorViewport } from "@deck.gl/core";
import { PathLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { LogRecord } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Map, { Marker } from "react-map-gl/maplibre";
import { MotorValues, motorValuesAt } from "../models/routeMotorData";
import {
  RouteBounds,
  RoutePoint,
  RouteSegment,
  SPEED_COLORS,
  TripRange,
  deviceOrder,
  routeBounds,
  speedColor,
  speedRange,
  toRoutePoints,
  toRouteSegments,
  toSegmentPath,
  toTripMarkers,
  vehicleColor,
} from "../models/routeTrack";
import { QueryDiagnosticDeviceData } from "@/models/QueryDiagnosticDeviceData";
import { useMapSettings } from "@/pages/map/hooks/useMapSettings";
import { DEFAULT_MAP_VIEWPORT, hexToRgba } from "@/pages/map/models/mapSettings";
import { MAP_STYLES } from "@/pages/map/models/mapStyles";
import "maplibre-gl/dist/maplibre-gl.css";

/** Enough of a gap that the outermost points are not under a legend. */
const FIT_PADDING = 40;
/** Above the map's own controls. */
const OVERLAY_Z_INDEX = 500;
const START_MARKER_COLOR = "#2e7d32";
const STOP_MARKER_COLOR = "#c62828";
const ROUTE_LINE_WIDTH_PX = 5;
/** Per cent, so a line crossing itself still shows what is underneath. */
const ROUTE_LINE_OPACITY = 90;
/** Used only if a colour helper ever hands back something unreadable. */
const FALLBACK_LINE_COLOR = "#1976d2";
/** How close the map comes when the whole route sits in one spot — a parked vehicle. */
const SINGLE_POINT_ZOOM = 15;

/** A box over the map, for the legends and the point details. */
function MapOverlay({ corner, children }: { corner: "topRight" | "bottomLeft"; children: ReactNode }) {
  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        zIndex: OVERLAY_Z_INDEX,
        ...(corner === "topRight" ? { top: 12, right: 12 } : { bottom: 12, left: 12 }),
        p: 1.25,
        minWidth: 200,
        maxWidth: 260,
        pointerEvents: "none",
      }}
    >
      {children}
    </Paper>
  );
}

/** One line of the point details, left out entirely when the vehicle reported nothing. */
function DetailLine({ label, value }: { label: string; value: string | null }) {
  if (value === null) {
    return null;
  }

  return (
    <Typography variant="caption" display="block">
      {`${label}: ${value}`}
    </Typography>
  );
}

interface Props {
  logRecords: LogRecord[];
  /** The journeys that were picked, which is what separates them from the rest of the day. */
  tripRanges: TripRange[];
  /** What to call each vehicle in the legend. */
  deviceLabels: Record<string, string>;
  /** One colour per vehicle instead of a colour per speed. */
  monochrome: boolean;
  /** The engine readings, shown for whichever point the reader clicks. */
  statusData?: QueryDiagnosticDeviceData;
  /** Whether positions are still on their way; the view follows them while they are. */
  isLoadingPositions?: boolean;
}

/**
 * The driven route on a map.
 *
 * The same MapLibre basemaps as the live map, so a reader's choice of map holds across the
 * add-in. The route itself is one set of data with a colour per piece rather than a drawn object
 * per piece: a day of driving is tens of thousands of them.
 */
export function TripRouteMap({
  logRecords,
  tripRanges,
  deviceLabels,
  monochrome,
  statusData,
  isLoadingPositions = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<RoutePoint | null>(null);
  const [viewState, setViewState] = useState<MapViewState>(() => ({
    longitude: DEFAULT_MAP_VIEWPORT.longitude,
    latitude: DEFAULT_MAP_VIEWPORT.latitude,
    zoom: DEFAULT_MAP_VIEWPORT.zoom,
  }));

  const containerRef = useRef<HTMLDivElement | null>(null);
  /** The set of journeys the view was last put around, so one pick does not move it twice. */
  const fittedKey = useRef<string | null>(null);
  const appliedSavedView = useRef(false);

  // The basemap and the last view come from the reader's live map settings, so both maps of one
  // fleet look the same and open in the same place.
  const { settings } = useMapSettings();
  const { mapStyle, viewport } = settings;

  const points = useMemo(() => toRoutePoints(logRecords, tripRanges), [logRecords, tripRanges]);
  const segments = useMemo(() => toRouteSegments(points), [points]);
  const speeds = useMemo(() => speedRange(points), [points]);
  const order = useMemo(() => deviceOrder(points), [points]);
  const markers = useMemo(() => toTripMarkers(tripRanges), [tripRanges]);

  // Without a route there is still a map: the reader's own basemap, looking where they left the
  // live map. An empty box says nothing, and the map is the point of the page.
  const hasRoute = points.length >= 2;

  /** Changes only when a different set of journeys is picked. */
  const fitKey = useMemo(
    () =>
      [...tripRanges]
        .map(range => range.tripId)
        .sort()
        .join("|"),
    [tripRanges],
  );

  // One layer for the whole route rather than a drawn object per piece: a day of driving is tens
  // of thousands of them, and this hands the lot to the graphics card in one go.
  const routeLayer = useMemo(
    () =>
      new PathLayer<RouteSegment>({
        id: "route",
        data: segments,
        pickable: true,
        widthUnits: "pixels",
        getWidth: ROUTE_LINE_WIDTH_PX,
        capRounded: true,
        jointRounded: true,
        getPath: toSegmentPath,
        getColor: segment =>
          hexToRgba(
            monochrome
              ? vehicleColor(segment.deviceId, order)
              : speedColor(segment.speed, speeds.min, speeds.max),
            ROUTE_LINE_OPACITY,
            FALLBACK_LINE_COLOR,
          ),
        // Colours are worked out once per piece and kept; without this, switching between one
        // colour per vehicle and one per speed would leave the old colours on screen.
        updateTriggers: { getColor: [monochrome, order, speeds.min, speeds.max] },
      }),
    [segments, monochrome, order, speeds],
  );

  // Only ever for the one point that was clicked: a day of positions is tens of thousands of
  // records, and the readings are never shown for more than one of them.
  const motor: MotorValues | null = useMemo(
    () => (selected ? motorValuesAt(statusData, selected.deviceId, selected.time) : null),
    [selected, statusData],
  );

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "medium" }),
    [i18n.language],
  );

  // A point picked from a journey that has since been unticked no longer exists on this map.
  useEffect(() => {
    setSelected(current =>
      current && points.some(point => point.time === current.time && point.deviceId === current.deviceId)
        ? current
        : null,
    );
  }, [points]);

  /** Moves the view to take in the whole route, measured against the box the map sits in. */
  const frameRoute = useCallback((bounds: RouteBounds) => {
    const container = containerRef.current;
    const width = container?.clientWidth ?? 0;
    const height = container?.clientHeight ?? 0;
    if (width === 0 || height === 0) {
      return;
    }

    const [[west, south], [east, north]] = bounds;

    // A vehicle that stood still all day has a route with no width at all, and asking for a box
    // of nothing to be framed answers with an infinite zoom.
    if (west === east && south === north) {
      setViewState(current => ({
        ...current,
        longitude: west,
        latitude: south,
        zoom: SINGLE_POINT_ZOOM,
      }));
      return;
    }

    // Padding has to leave something to look at: on a narrow pane a fixed inset can be wider
    // than the pane itself, which is rejected outright.
    const padding = Math.min(FIT_PADDING, Math.floor(Math.min(width, height) / 4));
    const fitted = new WebMercatorViewport({ width, height }).fitBounds(bounds, { padding });

    setViewState(current => ({
      ...current,
      longitude: fitted.longitude,
      latitude: fitted.latitude,
      zoom: fitted.zoom,
    }));
  }, []);

  // Where the reader left the live map, once their settings have arrived — they land a moment
  // after the first render. Only while there is no route: a route frames itself, and moving to a
  // stored view afterwards would take the reader away from what they asked to see.
  useEffect(() => {
    if (hasRoute || appliedSavedView.current || viewport === null) {
      return;
    }

    appliedSavedView.current = true;
    setViewState(current => ({
      ...current,
      longitude: viewport.longitude,
      latitude: viewport.latitude,
      zoom: viewport.zoom,
    }));
  }, [hasRoute, viewport]);

  // The positions arrive one request per vehicle, so the view follows them while they are still
  // coming in and the last framing is the one that counts. After that the map is left alone:
  // moving it again would undo the reader's own panning.
  useEffect(() => {
    const bounds = routeBounds(points);
    if (bounds === null || fittedKey.current === fitKey) {
      return;
    }

    if (!isLoadingPositions) {
      fittedKey.current = fitKey;
    }

    frameRoute(bounds);
  }, [points, fitKey, isLoadingPositions, frameRoute]);

  const showVehicleLegend = hasRoute && monochrome && order.length > 1;

  return (
    <Box ref={containerRef} sx={{ position: "relative", height: "100%", width: "100%" }}>
      <DeckGL
        // Asked for by name, as the live map does: left to itself the drawing layer looks for a
        // graphics device of its own choosing and can end up without one at all.
        deviceProps={{ type: "webgl" }}
        viewState={viewState}
        onViewStateChange={event => setViewState(event.viewState as MapViewState)}
        controller={true}
        layers={[routeLayer]}
        onClick={info => setSelected((info.object as RouteSegment | undefined)?.to ?? null)}
        getCursor={({ isDragging, isHovering }) =>
          isDragging ? "grabbing" : isHovering ? "pointer" : "grab"
        }
        style={{ height: "100%", width: "100%" }}
      >
        {/* The basemap is the reader's own choice from the live map, drawn by the same engine.
            The route rides above it as a deck.gl layer rather than as map data of its own. */}
        <Map mapStyle={MAP_STYLES[mapStyle]}>
          {markers.flatMap(marker => {
          const inRange = points.filter(
            point =>
              point.deviceId === marker.deviceId &&
              point.time >= marker.start.getTime() &&
              point.time <= marker.stop.getTime(),
          );
          const first = inRange[0];
          const last = inRange[inRange.length - 1];

          if (!first || !last) {
            return [];
          }

          return [
            <Marker
              key={`${marker.tripId}-start`}
              longitude={first.longitude}
              latitude={first.latitude}
            >
              <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: START_MARKER_COLOR,
                    border: "2px solid #fff",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    bottom: "100%",
                    mb: 0.25,
                    px: 0.5,
                    borderRadius: 0.5,
                    bgcolor: "background.paper",
                    fontWeight: 700,
                    lineHeight: 1.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  {marker.number}
                </Typography>
              </Box>
            </Marker>,
            <Marker key={`${marker.tripId}-stop`} longitude={last.longitude} latitude={last.latitude}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: STOP_MARKER_COLOR,
                  border: "2px solid #fff",
                }}
              />
            </Marker>,
          ];
        })}
        </Map>
      </DeckGL>

      {showVehicleLegend ? (
        <MapOverlay corner="topRight">
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            {t("route.vehicleLegend")}
          </Typography>
          {order.map(deviceId => (
            <Stack key={deviceId} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: vehicleColor(deviceId, order),
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" noWrap>
                {deviceLabels[deviceId] ?? deviceId}
              </Typography>
            </Stack>
          ))}
        </MapOverlay>
      ) : null}

      {!hasRoute || monochrome ? null : (
        <MapOverlay corner="topRight">
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
            {t("route.speedLegend")}
          </Typography>
          <Box
            sx={{
              height: 8,
              borderRadius: 1,
              my: 0.5,
              background: `linear-gradient(to right, ${SPEED_COLORS.join(", ")})`,
            }}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">
              {t("route.speedValue", { value: Math.round(speeds.min) })}
            </Typography>
            <Typography variant="caption">
              {t("route.speedValue", { value: Math.round(speeds.max) })}
            </Typography>
          </Stack>
        </MapOverlay>
      )}

      <MapOverlay corner="bottomLeft">
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
          {t("route.pointDetails")}
        </Typography>

        {/* Why there is no line on the map, said where the details would be: nothing picked yet,
            or nothing driven in the period that was picked. */}
        {hasRoute ? null : (
          <Typography variant="caption" color="text.secondary">
            {tripRanges.length === 0 ? t("route.selectTrip") : t("route.noRouteData")}
          </Typography>
        )}

        {selected && motor ? (
          <>
            <DetailLine label={t("route.time")} value={dateFormat.format(new Date(selected.time))} />
            <DetailLine
              label={t("route.location")}
              value={t("route.coordinates", {
                lat: selected.latitude.toFixed(5),
                lng: selected.longitude.toFixed(5),
              })}
            />
            <DetailLine
              label={t("route.speed")}
              value={t("route.speedValue", { value: Math.round(selected.speed) })}
            />
            <DetailLine
              label={t("route.engineSpeed")}
              value={
                motor.engineSpeed === null
                  ? null
                  : t("route.engineSpeedValue", { value: Math.round(motor.engineSpeed) })
              }
            />
            <DetailLine
              label={t("route.ignition")}
              value={motor.ignition === null ? null : motor.ignition ? t("yes") : t("no")}
            />
            <DetailLine
              label={t("route.engineRoadSpeed")}
              value={
                motor.engineRoadSpeed === null
                  ? null
                  : t("route.speedValue", { value: Math.round(motor.engineRoadSpeed) })
              }
            />
            <DetailLine
              label={t("route.fuelLevel")}
              value={
                motor.fuelLevel === null
                  ? null
                  : t("route.percentValue", { value: Math.round(motor.fuelLevel) })
              }
            />
            <DetailLine
              label={t("route.adBlueLevel")}
              value={
                motor.adBlueLevel === null
                  ? null
                  : t("route.percentValue", { value: Math.round(motor.adBlueLevel) })
              }
            />
            <DetailLine
              label={t("route.soc")}
              value={motor.soc === null ? null : t("route.percentValue", { value: Math.round(motor.soc) })}
            />
          </>
        ) : null}

        {hasRoute && !(selected && motor) ? (
          <Typography variant="caption" color="text.secondary">
            {t("route.selectMapPoint")}
          </Typography>
        ) : null}
      </MapOverlay>
    </Box>
  );
}
